import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { secondAxios } from "../../../../../../../api/axios";

const MRQContext = createContext(null);

const initialState = {
  mrqData: null,
  isLoading: false,
  isLoaded: false,
  isError: false,
  error: null,
  isEditMode: false,
};

export default function MRQProvider({ children }) {
  const { plantId } = useParams();

  const activeRequestRef = useRef(null);
  const requestVersionRef = useRef(0);

  const [state, setState] = useState(initialState);

  const loadMRQData = useCallback(
    async ({ force = false } = {}) => {
      if (!plantId) {
        setState(initialState);
        return initialState;
      }

      if (activeRequestRef.current && !force) {
        return activeRequestRef.current;
      }

      const requestVersion = requestVersionRef.current + 1;
      requestVersionRef.current = requestVersion;

      setState((prev) => ({
        ...prev,
        isLoading: true,
        isError: false,
        error: null,
      }));

      const request = secondAxios
        .get(`/mrq/${plantId}/materials`)
        .then((response) => {
          const raw = response?.data;
          const data = raw
            ? {
                id: raw.id,
                mrqNumber: raw.mrqNumber,
                mrqDate: raw.mrqDate,
                rev: raw.rev,
                mrqMaterials: raw.mrqMaterials,
              }
            : null;

          if (requestVersionRef.current === requestVersion) {
            setState((prev) => ({
              ...prev,
              mrqData: data,
              isLoading: false,
              isLoaded: true,
              isError: false,
              error: null,
            }));
          }

          return { mrqData: data, isLoaded: true, isError: false };
        })
        .catch((error) => {
          if (requestVersionRef.current === requestVersion) {
            setState((prev) => ({
              ...prev,
              isLoading: false,
              isLoaded: false,
              isError: true,
              error,
            }));
          }

          throw error;
        })
        .finally(() => {
          if (requestVersionRef.current === requestVersion) {
            activeRequestRef.current = null;
          }
        });

      activeRequestRef.current = request;
      return request;
    },
    [plantId],
  );

  const [isEditMode, setIsEditMode] = useState(false);

  const onChangeDetails = useCallback((e) => {
    const { name, value } = e.target;
    setState((prev) => ({
      ...prev,
      mrqData: prev.mrqData ? { ...prev.mrqData, [name]: value } : null,
    }));
  }, []);

  const onChangeCell = useCallback((rowIndex, columnId, value) => {
    setState((prev) => {
      if (!prev.mrqData) return prev;
      const updatedMaterials = [...(prev.mrqData.mrqMaterials || [])];
      updatedMaterials[rowIndex] = {
        ...updatedMaterials[rowIndex],
        [columnId]: value,
      };
      return {
        ...prev,
        mrqData: { ...prev.mrqData, mrqMaterials: updatedMaterials },
      };
    });
  }, []);

  useEffect(() => {
    loadMRQData().catch(() => {});
  }, [loadMRQData]);

  const refreshMRQData = useCallback(() => {
    return loadMRQData({ force: true });
  }, [loadMRQData]);

  const value = useMemo(
    () => ({
      ...state,
      plantId,
      isEditMode,
      setIsEditMode,
      onChangeDetails,
      onChangeCell,
      loadMRQData,
      refreshMRQData,
    }),
    [state, plantId, isEditMode, onChangeDetails, onChangeCell, loadMRQData, refreshMRQData],
  );

  return <MRQContext.Provider value={value}>{children}</MRQContext.Provider>;
}

export function useMRQContext() {
  const context = useContext(MRQContext);

  if (!context) {
    throw new Error("useMRQContext must be used inside MRQProvider");
  }

  return context;
}
